import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Conversation, Participant } from '../types';

const roleLabel = (role: Participant['role']) => {
  switch (role) {
    case 'admin':
      return 'Administrator';
    case 'teacher':
      return 'Profesor';
    case 'student':
      return 'Elev';
    case 'parent':
      return 'Părinte';
    default:
      return role;
  }
};

const parentChildrenText = (p: Participant) => {
  if (p.role !== 'parent') return null;
  const children = p.children ?? [];
  if (children.length === 0) return null;
  return children
    .map((c) => `${c.name}${c.classroomName ? ` (${c.classroomName})` : ''}`)
    .join(', ');
};

export default function GroupParticipantsModal({
  open,
  onClose,
  conversation,
  onOpenProfile,
}: {
  open: boolean;
  onClose: () => void;
  conversation: Conversation | null;
  onOpenProfile: (userId: string) => void;
}) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const participants = useMemo(() => {
    if (!conversation) return [];
    const list = conversation.participants ?? [];
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return list.slice().sort((a, b) => a.name.localeCompare(b.name, 'ro-RO'));

    return list
      .filter((p) => {
        if (p.name.toLowerCase().includes(trimmed)) return true;
        const childText = parentChildrenText(p);
        if (childText && childText.toLowerCase().includes(trimmed)) return true;
        return roleLabel(p.role).toLowerCase().includes(trimmed);
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'ro-RO'));
  }, [conversation, query]);

  if (!open) return null;
  if (!conversation) return null;

  return (
    <div
      className="fixed inset-0 z-[1190] flex items-start justify-center px-4 py-6 sm:py-8 overflow-y-auto bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Participanți"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl max-h-[calc(100vh-3rem)] sm:max-h-[calc(100vh-4rem)] bg-card rounded-lg shadow-lg flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-border flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-foreground truncate">Participanți</h2>
            <p className="text-xs text-muted-foreground">
              {conversation.name} • {conversation.participants.length}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
            aria-label="Închide"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-3 sm:p-4 border-b border-border flex-shrink-0">
          <Input
            type="search"
            placeholder="Caută participanți..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {participants.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4">
              <Icon name="Users" size={48} className="text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">Nu am găsit participanți.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {participants.map((p) => {
                const children = parentChildrenText(p);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      onClose();
                      onOpenProfile(p.id);
                    }}
                    className="w-full text-left p-3 sm:p-4 hover:bg-muted/60 transition-colors"
                    aria-label={`Vezi profil: ${p.name}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative flex-shrink-0">
                        {p.avatar ? (
                          <Image
                            src={p.avatar}
                            alt="Avatar"
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                            {p.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        {p.isOnline && (
                          <div className="absolute bottom-0 right-0 w-3 h-3 bg-success rounded-full border-2 border-card" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                          <span className="text-xs text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground">{roleLabel(p.role)}</span>
                        </div>
                        {children ? (
                          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                            Copii: {children}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex-shrink-0 pt-1">
                        <Icon name="ChevronRight" size={18} className="text-muted-foreground" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-3 sm:p-4 border-t border-border bg-card flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Închide
          </Button>
        </div>
      </div>
    </div>
  );
}

